import { assertUnreachable, isNotDefined } from '@sgme/fp';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { formatPremium } from '@/App/tiles/Option/utils/premiumUtils.ts';
import { useAppSelector } from '@/store/hooks.ts';
import type { PremiumCategory } from '@/store/option/state.ts';

export function useFormatPremium(currencyPair: string) {
  const { formatNumber } = useIntl();
  const currencyPairPrecision = useAppSelector((state) => selectCurrencyPairPrecision(state, currencyPair));

  const format = useCallback(
    (value: number | null | undefined, premiumCategory: PremiumCategory): string => {
      if (isNotDefined(value)) {
        return '';
      }
      const premiumValue = formatPremium(value, premiumCategory, currencyPairPrecision);

      const formatPrecision = getPremiumPrecision(premiumCategory);
      const val = formatNumber(premiumValue, {
        minimumFractionDigits: formatPrecision,
        maximumFractionDigits: formatPrecision,
      });
      return `${val}`;
    },
    [currencyPairPrecision, formatNumber],
  );

  return format;
}

function getPremiumPrecision(type: PremiumCategory): number {
  switch (type) {
    case 'VOLATILITY':
      return 2;
    case 'AMOUNT':
      return 2;
    case 'PERCENT':
      return 4;
    case 'PPS':
      return 4;
    default:
      assertUnreachable(type, `Unknown premium category: ${type}`);
  }
}
